import { isUndefined, omit, omitBy } from 'lodash';
import { Document, FilterQuery, Model, PipelineStage, Types, UpdateQuery, UpdateWithAggregationPipeline } from 'mongoose';

import { QueryOptions } from '../types';

export abstract class BaseRepository<T extends Document> {
  protected constructor(public readonly model: Model<T>) {}

  public async aggregate(pipeline?: PipelineStage[]): Promise<any[]> {
    return this.model.aggregate(pipeline, { allowDiskUse: true });
  }

  public async count(filter: FilterQuery<T> = {}): Promise<number> {
    return this.model.countDocuments(omitBy(filter, isUndefined) as FilterQuery<T>);
  }

  public async create(object: object, { lean = true, populateOptions }: QueryOptions<T> = {}): Promise<T> {
    const document = await new this.model(object).save();
    if (populateOptions) await document.populate(populateOptions);
    return (lean ? document.toObject() : document) as unknown as T;
  }

  public async distinct(field: string, filter: FilterQuery<T> = {}): Promise<any[]> {
    return this.model.distinct(field, omitBy(filter, isUndefined) as FilterQuery<T>);
  }

  public async find(
    filter: FilterQuery<T> = {},
    { lean = true, page = 1, populateOptions, projection, size = 100, sort = '_id' }: QueryOptions<T> = {}
  ): Promise<T[]> {
    const query = this.model.find(omitBy(filter, isUndefined) as FilterQuery<T>, projection, { lean, limit: size, skip: (page - 1) * size, sort });
    if (populateOptions) void query.populate(populateOptions);
    return query.exec();
  }

  public async findById(
    id: Types.ObjectId,
    filter: FilterQuery<T> = {},
    { lean = true, populateOptions, projection }: QueryOptions<T> = {}
  ): Promise<T | null> {
    if (!id) return null;
    const query = this.model.findOne(omitBy({ _id: id, ...filter }, isUndefined) as FilterQuery<T>, projection, { lean });
    if (populateOptions) void query.populate(populateOptions);
    return query.exec();
  }

  public async findByIdAndDelete(id: Types.ObjectId, force = false, filter: FilterQuery<T> = {}, { lean = true }: QueryOptions<T> = {}): Promise<T | null> {
    if (!id) return null;
    if (force) return this.model.findOneAndDelete(omit(omitBy({ _id: id, ...filter }, isUndefined), 'deleted') as FilterQuery<T>, { lean }).exec();
    return this.model.findOneAndUpdate(omitBy({ _id: id, ...filter }, isUndefined) as FilterQuery<T>, { deleted: true }, { lean, new: true }).exec();
  }

  public async findByIdAndUpdate(
    id: Types.ObjectId,
    object: object,
    filter: FilterQuery<T> = {},
    { lean = true, populateOptions, projection, upsert = false }: QueryOptions<T> = {}
  ): Promise<T | null> {
    if (!id) return null;
    const query = this.model.findOne(omitBy({ _id: id, ...filter }, isUndefined) as FilterQuery<T>, projection);
    if (populateOptions) void query.populate(populateOptions);
    let document = await query.exec();
    if (!document) return upsert ? this.create({ ...object, _id: id }, { lean, populateOptions }) : null;
    document = Object.assign(document, object);
    document = await document.save();
    return (lean ? document.toObject() : document) as unknown as T;
  }

  public async findOne(filter: FilterQuery<T>, { lean = true, populateOptions, projection }: QueryOptions<T> = {}): Promise<T | null> {
    const query = this.model.findOne(omitBy(filter, isUndefined) as FilterQuery<T>, projection, { lean });
    if (populateOptions) void query.populate(populateOptions);
    return query.exec();
  }

  public async updateMany(filter: FilterQuery<T>, update: UpdateWithAggregationPipeline | UpdateQuery<T>): Promise<number> {
    const result = await this.model.updateMany(omitBy(filter, isUndefined) as FilterQuery<T>, update);
    return result.modifiedCount;
  }
}
